import { tokenStore } from "./tokenStore";
import type {
  AuthCode,
  CodeVerifier,
  OAuthState,
  RefreshToken,
  TokenResponse,
} from "./types";
import { AuthError } from "./types";

const SESSION_KEYS = {
  CODE_VERIFIER: "3t-bridge:auth:pkce-verifier",
  OAUTH_STATE: "3t-bridge:auth:oauth-state",
  PROVIDER_ID: "3t-bridge:auth:provider-id",
} as const;

function safeSessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Best effort — sessionStorage may be blocked (iframe, privacy mode)
  }
}

function safeSessionRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Best effort — don't throw on cleanup
  }
}

// ---------------------------------------------------------------------------
// Provider resolution
// ---------------------------------------------------------------------------

const SAFE_ID_PATTERN = /^[A-Za-z0-9._-]+$/;

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

function hasValidProviderId(id: unknown): id is string {
  return typeof id === "string" && SAFE_ID_PATTERN.test(id);
}

function isOptionalString(v: unknown): boolean {
  return v === undefined || (typeof v === "string" && v.trim() !== "");
}

function hasRequiredProviderFields(obj: Record<string, unknown>): boolean {
  return (
    hasValidProviderId(obj.id) &&
    isNonEmptyString(obj.name) &&
    isNonEmptyString(obj.authority) &&
    isNonEmptyString(obj.clientId)
  );
}

function isValidEndpoints(v: unknown): boolean {
  if (v === undefined) return true;
  if (typeof v !== "object" || v === null) return false;
  const e = v as Record<string, unknown>;
  return (
    isNonEmptyString(e.authorization_endpoint) &&
    isNonEmptyString(e.token_endpoint) &&
    (e.end_session_endpoint === undefined ||
      e.end_session_endpoint === null ||
      isNonEmptyString(e.end_session_endpoint))
  );
}

function isValidProvider(p: unknown): p is OidcProvider {
  if (typeof p !== "object" || p === null) return false;
  const obj = p as Record<string, unknown>;
  return (
    hasRequiredProviderFields(obj) &&
    isOptionalString(obj.allowedDomains) &&
    isOptionalString(obj.orgId) &&
    isOptionalString(obj.idpHint) &&
    isValidEndpoints(obj.endpoints)
  );
}

function getRuntimeProviders(): OidcProvider[] {
  const raw = window.__3T_BRIDGE_CONFIG__?.oidcProviders;
  if (!Array.isArray(raw)) return [];
  return raw.filter(isValidProvider);
}

function getDevProvider(): OidcProvider | null {
  const authority = import.meta.env?.["3T_BRIDGE_OIDC_AUTHORITY"] as
    | string
    | undefined;
  const clientId = import.meta.env?.["3T_BRIDGE_OIDC_CLIENT_ID"] as
    | string
    | undefined;
  if (!authority || !clientId) return null;

  return {
    id: import.meta.env?.["3T_BRIDGE_OIDC_PROVIDER_ID"] ?? "dev",
    name: import.meta.env?.["3T_BRIDGE_OIDC_PROVIDER_NAME"] ?? "Login",
    authority,
    clientId,
    allowedDomains: import.meta.env?.["3T_BRIDGE_OIDC_ALLOWED_DOMAINS"],
    endpoints: getDevEndpoints(),
  };
}

/**
 * Explicit OIDC endpoints from env, for local dev against authorities that
 * don't serve `.well-known` discovery (e.g. AWS Cognito custom domains).
 * Returns `undefined` unless both authorization + token endpoints are set, so
 * the default path (discovery) is unaffected when they're absent.
 */
function getDevEndpoints(): OidcEndpoints | undefined {
  const authorization_endpoint = import.meta.env?.[
    "3T_BRIDGE_OIDC_AUTHORIZATION_ENDPOINT"
  ] as string | undefined;
  const token_endpoint = import.meta.env?.["3T_BRIDGE_OIDC_TOKEN_ENDPOINT"] as
    | string
    | undefined;
  const end_session_endpoint = import.meta.env?.[
    "3T_BRIDGE_OIDC_END_SESSION_ENDPOINT"
  ] as string | undefined;

  if (!authorization_endpoint || !token_endpoint) return undefined;
  return {
    authorization_endpoint,
    token_endpoint,
    end_session_endpoint: end_session_endpoint ?? null,
  };
}

export function getProviders(): OidcProvider[] {
  const runtime = getRuntimeProviders();
  if (runtime.length > 0) return runtime;

  const dev = getDevProvider();
  return dev ? [dev] : [];
}

export function getProviderById(id: string): OidcProvider | undefined {
  return getProviders().find((p) => p.id === id);
}

export function isAuthConfigured(): boolean {
  return getProviders().length > 0;
}

// ---------------------------------------------------------------------------
// OIDC Discovery (per-provider cache)
// ---------------------------------------------------------------------------

type OidcEndpoints = {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string | null;
};

const endpointsCache = new Map<string, OidcEndpoints>();
const discoveryPromises = new Map<string, Promise<OidcEndpoints>>();

const AUTH_CALLBACK_PATH = "/auth/callback";

function requireString(
  obj: Record<string, unknown>,
  key: string,
  discoveryUrl: string,
): string {
  const value = obj[key];
  if (typeof value !== "string" || value.trim() === "") {
    throw new AuthError(
      `OIDC discovery response from ${discoveryUrl} is missing required field "${key}"`,
      null,
    );
  }
  return value;
}

async function fetchDiscovery(authority: string): Promise<OidcEndpoints> {
  const base = authority.replace(/\/+$/, "");
  const discoveryUrl = `${base}/.well-known/openid-configuration`;

  const response = await fetch(discoveryUrl);
  if (!response.ok) {
    throw new AuthError(
      `OIDC discovery request failed (${response.status}): ${discoveryUrl}`,
      response.status,
    );
  }

  const doc: Record<string, unknown> = await response.json();

  return {
    authorization_endpoint: requireString(
      doc,
      "authorization_endpoint",
      discoveryUrl,
    ),
    token_endpoint: requireString(doc, "token_endpoint", discoveryUrl),
    end_session_endpoint:
      typeof doc.end_session_endpoint === "string" &&
      doc.end_session_endpoint.trim() !== ""
        ? doc.end_session_endpoint
        : null,
  };
}

async function getEndpoints(provider: OidcProvider): Promise<OidcEndpoints> {
  // Explicit endpoints bypass .well-known discovery entirely — required for
  // authorities that serve no discovery doc (e.g. AWS Cognito custom domains).
  if (provider.endpoints) {
    return {
      authorization_endpoint: provider.endpoints.authorization_endpoint,
      token_endpoint: provider.endpoints.token_endpoint,
      end_session_endpoint: provider.endpoints.end_session_endpoint ?? null,
    };
  }

  const cached = endpointsCache.get(provider.id);
  if (cached) return cached;

  let promise = discoveryPromises.get(provider.id);
  if (!promise) {
    promise = fetchDiscovery(provider.authority)
      .then((endpoints) => {
        endpointsCache.set(provider.id, endpoints);
        return endpoints;
      })
      .finally(() => {
        discoveryPromises.delete(provider.id);
      });
    discoveryPromises.set(provider.id, promise);
  }

  return promise;
}

export function resetEndpointsCache(): void {
  endpointsCache.clear();
  discoveryPromises.clear();
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(
    Array.from(buffer, (byte) => String.fromCharCode(byte)).join(""),
  );
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function generateRandom(): string {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

// ---------------------------------------------------------------------------
// Session state (code verifier, OAuth state, provider ID)
// ---------------------------------------------------------------------------

function storeCodeVerifier(verifier: CodeVerifier): void {
  safeSessionSet(SESSION_KEYS.CODE_VERIFIER, verifier);
}

export function getCodeVerifier(): CodeVerifier | null {
  const value = safeSessionGet(SESSION_KEYS.CODE_VERIFIER);
  return value !== null ? (value as CodeVerifier) : null;
}

export function clearCodeVerifier(): void {
  safeSessionRemove(SESSION_KEYS.CODE_VERIFIER);
}

export function clearOAuthState(): void {
  clearCodeVerifier();
  safeSessionRemove(SESSION_KEYS.OAUTH_STATE);
  safeSessionRemove(SESSION_KEYS.PROVIDER_ID);
}

export function getStoredProviderId(): string | null {
  return safeSessionGet(SESSION_KEYS.PROVIDER_ID);
}

export function verifyOAuthState(received: OAuthState): boolean {
  const stored = safeSessionGet(SESSION_KEYS.OAUTH_STATE);
  if (!stored || stored !== received) {
    safeSessionRemove(SESSION_KEYS.CODE_VERIFIER);
    return false;
  }
  safeSessionRemove(SESSION_KEYS.OAUTH_STATE);
  return true;
}

// ---------------------------------------------------------------------------
// Redirect URI
// ---------------------------------------------------------------------------

function getRedirectUri(): string {
  return `${window.location.origin}${AUTH_CALLBACK_PATH}`;
}

// ---------------------------------------------------------------------------
// Login URL
// ---------------------------------------------------------------------------

function buildScope(provider: OidcProvider): string {
  const base = "openid profile email offline_access";
  if (provider.orgId) {
    return `${base} urn:zitadel:iam:org:id:${provider.orgId}`;
  }
  return base;
}

function buildAuthParams(
  provider: OidcProvider,
  codeChallenge: string,
  state: string,
): URLSearchParams {
  const params = new URLSearchParams({
    client_id: provider.clientId,
    redirect_uri: getRedirectUri(),
    response_type: "code",
    scope: buildScope(provider),
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state,
  });

  if (provider.idpHint) params.set("idp_hint", provider.idpHint);

  const firstDomain = provider.allowedDomains?.split(",")[0]?.trim();
  if (firstDomain) params.set("hd", firstDomain);

  return params;
}

export async function buildLoginUrl(provider: OidcProvider): Promise<string> {
  const { authorization_endpoint } = await getEndpoints(provider);

  const codeVerifier = generateRandom();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandom();

  storeCodeVerifier(codeVerifier as CodeVerifier);
  safeSessionSet(SESSION_KEYS.OAUTH_STATE, state);
  safeSessionSet(SESSION_KEYS.PROVIDER_ID, provider.id);

  const params = buildAuthParams(provider, codeChallenge, state);
  return `${authorization_endpoint}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Token exchange
// ---------------------------------------------------------------------------

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

function optionalNumber(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function parseTokenResponse(data: Record<string, unknown>): TokenResponse {
  const accessToken = data.access_token;
  if (typeof accessToken !== "string" || accessToken.trim() === "") {
    throw new AuthError("Invalid access_token in token response", null);
  }

  return {
    access_token: accessToken,
    refresh_token: optionalString(data.refresh_token),
    id_token: optionalString(data.id_token),
    expires_in: optionalNumber(data.expires_in),
    token_type: optionalString(data.token_type),
  };
}

async function postToTokenEndpoint(
  provider: OidcProvider,
  params: URLSearchParams,
): Promise<TokenResponse> {
  const tokenUrl = import.meta.env.DEV
    ? (await getEndpoints(provider)).token_endpoint
    : `/oidc/token/${encodeURIComponent(provider.id)}`;

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => response.statusText);
    throw new AuthError(
      `Token request failed (${response.status}): ${body}`,
      response.status,
    );
  }

  return parseTokenResponse(await response.json());
}

export async function exchangeCodeForTokens(
  provider: OidcProvider,
  code: AuthCode,
  codeVerifier: CodeVerifier,
): Promise<TokenResponse> {
  const params = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: getRedirectUri(),
    client_id: provider.clientId,
    code_verifier: codeVerifier,
  });

  return postToTokenEndpoint(provider, params);
}

export async function refreshAccessToken(
  provider: OidcProvider,
  refreshToken: RefreshToken,
): Promise<TokenResponse> {
  const params = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: provider.clientId,
  });

  return postToTokenEndpoint(provider, params);
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

export async function buildLogoutUrl(
  provider: OidcProvider,
  idToken?: string | null,
): Promise<string | null> {
  const { end_session_endpoint } = await getEndpoints(provider);
  if (!end_session_endpoint) return null;

  const params = new URLSearchParams({
    post_logout_redirect_uri: `${window.location.origin}/`,
    client_id: provider.clientId,
  });

  if (idToken) {
    params.set("id_token_hint", idToken);
  }

  return `${end_session_endpoint}?${params.toString()}`;
}

export async function redirectToLogout(
  provider: OidcProvider,
  idToken?: string | null,
): Promise<boolean> {
  const logoutUrl = await buildLogoutUrl(provider, idToken);
  if (!logoutUrl) return false;
  window.location.href = logoutUrl;
  return true;
}

// ---------------------------------------------------------------------------
// Login redirect
// ---------------------------------------------------------------------------

export async function redirectToLogin(
  provider: OidcProvider,
  returnTo?: string,
): Promise<void> {
  if (returnTo && returnTo !== AUTH_CALLBACK_PATH) {
    tokenStore.setRedirectLocation(returnTo);
  }

  const loginUrl = await buildLoginUrl(provider);
  window.location.href = loginUrl;

  return new Promise<void>((_, reject) => {
    window.setTimeout(() => {
      reject(
        new AuthError(
          "Navigation to the login page was blocked or timed out",
          null,
        ),
      );
    }, 10_000);
  });
}
